import axios, { AxiosHeaders } from 'axios';
import type {
  BackupFile,
  BackupStats,
  BackupLog,
  BackupStatus,
  RcloneRemote,
  BackupExecutionResult
} from '../types/backup';

// Em dev, usa path relativo para o proxy do Vite funcionar
const API_URL = import.meta.env.DEV ? '' : (import.meta.env.VITE_API_URL || '');

const api = axios.create({
  baseURL: `${API_URL}/api/backup`,
});

// Interceptor para adicionar token de autenticação
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('accessToken'); // Fixed: use 'accessToken' not 'token'

  if (!config.headers) {
    config.headers = new AxiosHeaders();
  }

  config.headers.set('Content-Type', 'application/json');

  if (token) {
    config.headers.set('Authorization', `Bearer ${token}`);
  }

  return config;
});

export class BackupService {
  /**
   * Lista todos os backups
   */
  static async listBackups(category?: 'daily' | 'weekly' | 'monthly' | 'csv'): Promise<BackupFile[]> {
    const params = category ? { category } : {};
    const response = await api.get('/list', { params });
    return response.data.data;
  }

  /**
   * Obtém estatísticas dos backups
   */
  static async getStats(): Promise<BackupStats> {
    const response = await api.get('/stats');
    return response.data.data;
  }

  /**
   * Obtém status do sistema de backup
   */
  static async getStatus(): Promise<BackupStatus> {
    const response = await api.get('/status');
    return response.data.data;
  }

  /**
   * Obtém logs de backup
   */
  static async getLogs(lines: number = 100): Promise<BackupLog[]> {
    const response = await api.get('/logs', { params: { lines } });
    return response.data.data;
  }

  /**
   * Executa backup manual
   */
  static async executeBackup(type: 'database' | 'files' | 'full' | 'csv'): Promise<BackupExecutionResult> {
    const response = await api.post('/execute', { type });
    return response.data.data;
  }

  /**
   * Lista remotes do rclone
   */
  static async listRcloneRemotes(): Promise<RcloneRemote[]> {
    const response = await api.get('/rclone/remotes');
    return response.data.data;
  }

  /**
   * Testa conexão com remote
   */
  static async testRcloneRemote(remoteName: string): Promise<BackupExecutionResult> {
    const response = await api.post('/rclone/test', { remoteName });
    return response.data.data;
  }

  /**
   * Faz upload para nuvem
   */
  static async uploadToCloud(): Promise<BackupExecutionResult> {
    const response = await api.post('/cloud/upload');
    return response.data.data;
  }

  /**
   * Verifica saúde do sistema
   */
  static async checkHealth(): Promise<BackupExecutionResult> {
    const response = await api.get('/health');
    return response.data.data;
  }
}
